// BienDetailPage — Fiche détaillée d'un bien en location (/bien/:id) :
// galerie photos, équipements, calendrier de disponibilité, réservation
// avec choix de la date, de la durée et de l'unité (nuit / jour / semaine…).
import React, { useEffect, useMemo, useState } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  ChevronLeft, ChevronRight, MapPin, Star, BadgeCheck, CheckCircle2, Check,
  CalendarDays, Minus, Plus, Truck, Wrench as WrenchIcon,
} from "lucide-react";
import { RENTALS_REGISTRY } from "@/verticals/verticalData";
import "@/verticals/vertical.css";

const API = `${process.env.REACT_APP_BACKEND_URL || ""}/api`;
const fmt = (n) => `${Number(n || 0).toLocaleString("fr-FR").replace(/\u202F|\u00A0/g, " ")} FCFA`;
const UNIT_DAYS = { nuit: 1, jour: 1, "week-end": 2, semaine: 7, mois: 30 };
const MONTHS = ["Janvier", "Février", "Mars", "Avril", "Mai", "Juin", "Juillet", "Août", "Septembre", "Octobre", "Novembre", "Décembre"];
const iso = (d) => `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;

// Jours indisponibles déterministes (démo calendrier de disponibilité).
const isBlocked = (bienId, y, m, day) => (day * 7 + m + bienId.length) % 11 === 0;

function Calendar({ bienId, month, onMonth, selected, onPick }) {
  const now = new Date();
  const first = new Date(month.y, month.m, 1);
  const startCol = (first.getDay() + 6) % 7;
  const nbDays = new Date(month.y, month.m + 1, 0).getDate();
  const todayIso = iso(now);
  const canPrev = month.y > now.getFullYear() || month.m > now.getMonth();
  const canNext = (month.y - now.getFullYear()) * 12 + (month.m - now.getMonth()) < 2;
  return (
    <div className="bd-cal" data-testid="bd-calendar">
      <div className="head">
        <button disabled={!canPrev} data-testid="bd-cal-prev" aria-label="Mois précédent" onClick={() => onMonth(-1)}><ChevronLeft size={15} /></button>
        <strong>{MONTHS[month.m]} {month.y}</strong>
        <button disabled={!canNext} data-testid="bd-cal-next" aria-label="Mois suivant" onClick={() => onMonth(1)}><ChevronRight size={15} /></button>
      </div>
      <div className="grid">
        {["L", "M", "M", "J", "V", "S", "D"].map((d, i) => <span key={i} className="wd">{d}</span>)}
        {Array.from({ length: startCol }).map((_, i) => <span key={`e${i}`} />)}
        {Array.from({ length: nbDays }).map((_, i) => {
          const day = i + 1;
          const dIso = iso(new Date(month.y, month.m, day));
          const past = dIso < todayIso;
          const blocked = isBlocked(bienId, month.y, month.m, day);
          const sel = selected === dIso;
          return (
            <button
              key={day}
              className={`day${sel ? " sel" : ""}${blocked && !past ? " blocked" : ""}`}
              disabled={past || blocked}
              data-testid={`bd-day-${dIso}`}
              onClick={() => onPick(dIso)}
            >{day}</button>
          );
        })}
      </div>
      <div className="legend">
        <span><i className="dot free" /> Disponible</span>
        <span><i className="dot busy" /> Indisponible</span>
      </div>
    </div>
  );
}

export default function BienDetailPage() {
  const { id } = useParams();
  const [params] = useSearchParams();
  const navigate = useNavigate();
  const bien = RENTALS_REGISTRY[id];
  const now = new Date();
  const [photo, setPhoto] = useState(0);
  const [month, setMonth] = useState({ y: now.getFullYear(), m: now.getMonth() });
  const [startDate, setStartDate] = useState("");
  const [unit, setUnit] = useState(bien?.rental_model?.units?.[0] || "jour");
  const [duration, setDuration] = useState(1);
  const [quantity, setQuantity] = useState(1);
  const [driver, setDriver] = useState(bien?.rental_model?.driver_options?.[0] || "");
  const [delivery, setDelivery] = useState(false);
  const [installation, setInstallation] = useState(false);
  const [sending, setSending] = useState(false);
  const [confirmed, setConfirmed] = useState(null);

  useEffect(() => {
    document.title = `AYA — ${bien?.name || "Location"}`;
    if (params.get("book") === "1") {
      setTimeout(() => document.querySelector('[data-testid="bd-booking"]')?.scrollIntoView({ behavior: "smooth" }), 400);
    } else {
      window.scrollTo(0, 0);
    }
  }, [bien, params]);

  const isEvent = !!bien?.rental_model?.event_fields;
  const total = useMemo(() => {
    if (!bien) return 0;
    let t = bien.price_fcfa * duration * (isEvent ? quantity : 1);
    if (delivery) t += 10000;
    if (installation) t += 15000;
    return t;
  }, [bien, duration, quantity, delivery, installation, isEvent]);

  const endDate = useMemo(() => {
    if (!startDate) return null;
    const d = new Date(startDate);
    d.setDate(d.getDate() + (UNIT_DAYS[unit] || 1) * duration);
    return iso(d);
  }, [startDate, unit, duration]);

  if (!bien) {
    return (
      <div className="vt-page bd-page" style={{ padding: 24 }}>
        <p style={{ color: "#ECF5EE" }}>Bien introuvable.</p>
        <button className="vt-pillbtn active" onClick={() => navigate("/location")}>Retour aux locations</button>
      </div>
    );
  }

  const submit = async () => {
    if (!startDate) { toast.error("Choisis une date de début dans le calendrier"); return; }
    setSending(true);
    try {
      const r = await axios.post(`${API}/v3/rentals/${id}/bookings`, {
        item_name: bien.name, unit, duration, start_date: startDate, end_date: endDate,
        quantity: isEvent ? quantity : 1, delivery, installation, total_fcfa: total,
        note: driver ? `Option : ${driver}` : "",
      });
      if (r.data?.ok) { setConfirmed(r.data.booking); toast.success("Réservation confirmée ✅"); }
      else toast.error("Réservation impossible, réessaie.");
    } catch (_) { toast.error("Réservation impossible, réessaie."); }
    setSending(false);
  };

  return (
    <div className="vt-page bd-page" data-testid={`bien-detail-${id}`}>
      {/* GALERIE */}
      <div className="bd-gallery">
        <img src={bien.photos[photo]} alt={bien.name} className="main" data-testid="bd-photo-main" />
        <button className="vt-back bd-back" data-testid="bd-back-btn" aria-label="Retour" onClick={() => navigate(bien.back)}><ChevronLeft size={18} /></button>
        <span className="bd-badge">{bien.price_prefix ? "À partir de " : ""}{fmt(bien.price_fcfa)} {bien.price_unit}</span>
        <div className="thumbs">
          {bien.photos.map((p, i) => (
            <button key={p} className={`th${photo === i ? " on" : ""}`} data-testid={`bd-thumb-${i}`} onClick={() => setPhoto(i)}>
              <img src={p} alt="" loading="lazy" />
            </button>
          ))}
        </div>
      </div>

      <div className="vt-container bd-body">
        {/* IDENTITÉ */}
        <h1 className="bd-name">{bien.name} {bien.verified && <BadgeCheck size={17} color="#2EE27B" />}</h1>
        <div className="bd-meta">
          <span className="rate"><Star size={12} color="#F5C445" fill="#F5C445" /> {bien.rating} <em>({bien.reviews} avis)</em></span>
          <span className="loc"><MapPin size={11} /> {bien.area} · {bien.distance}</span>
        </div>
        <div className="tags bd-tags">{bien.tags.map((t) => <i key={t}>{t}</i>)}</div>
        <p className="bd-desc">{bien.desc}</p>

        {/* ÉQUIPEMENTS */}
        <h2 className="pp-h2"><WrenchIcon size={15} color="#F97316" /> Équipements inclus</h2>
        <div className="bd-equips" data-testid="bd-equipments">
          {bien.equipments.map((e) => <span key={e}><Check size={12} color="#2EE27B" /> {e}</span>)}
        </div>

        {/* CALENDRIER */}
        <h2 className="pp-h2"><CalendarDays size={15} color="#2EE27B" /> Disponibilités</h2>
        <Calendar
          bienId={id}
          month={month}
          onMonth={(d) => setMonth((m) => { const nm = new Date(m.y, m.m + d, 1); return { y: nm.getFullYear(), m: nm.getMonth() }; })}
          selected={startDate}
          onPick={setStartDate}
        />

        {/* RÉSERVATION */}
        <h2 className="pp-h2"><CalendarDays size={15} color="#F97316" /> Réserver</h2>
        <div className="bd-booking" data-testid="bd-booking">
          <label className="lab">Durée</label>
          <div className="units">
            {bien.rental_model.units.map((u) => (
              <button key={u} className={`u${unit === u ? " on" : ""}`} data-testid={`bd-unit-${u.replace(/\s/g, "")}`} onClick={() => setUnit(u)}>{u}</button>
            ))}
            <div className="stepper" data-testid="bd-duration-stepper">
              <button aria-label="Moins" data-testid="bd-duration-minus" onClick={() => setDuration((d) => Math.max(1, d - 1))}><Minus size={13} /></button>
              <strong data-testid="bd-duration-value">{duration}</strong>
              <button aria-label="Plus" data-testid="bd-duration-plus" onClick={() => setDuration((d) => Math.min(30, d + 1))}><Plus size={13} /></button>
            </div>
          </div>
          {bien.rental_model.driver_options && (
            <>
              <label className="lab">Conduite</label>
              <div className="units">
                {bien.rental_model.driver_options.map((o) => (
                  <button key={o} className={`u${driver === o ? " on" : ""}`} data-testid={`bd-driver-${o.replace(/\s/g, "-")}`} onClick={() => setDriver(o)}>{o}</button>
                ))}
              </div>
            </>
          )}
          {isEvent && (
            <>
              <label className="lab">Quantité</label>
              <div className="units">
                <div className="stepper" data-testid="bd-qty-stepper">
                  <button aria-label="Moins" onClick={() => setQuantity((q) => Math.max(1, q - 1))}><Minus size={13} /></button>
                  <strong data-testid="bd-qty-value">{quantity}</strong>
                  <button aria-label="Plus" onClick={() => setQuantity((q) => Math.min(20, q + 1))}><Plus size={13} /></button>
                </div>
                <button className={`u opt${delivery ? " on" : ""}`} data-testid="bd-delivery-toggle" onClick={() => setDelivery((v) => !v)}><Truck size={12} /> Livraison (+10 000 F)</button>
                <button className={`u opt${installation ? " on" : ""}`} data-testid="bd-installation-toggle" onClick={() => setInstallation((v) => !v)}><WrenchIcon size={12} /> Installation (+15 000 F)</button>
              </div>
            </>
          )}
          <div className="recap" data-testid="bd-recap">
            {startDate
              ? <>Du <strong>{startDate}</strong> au <strong>{endDate}</strong> · {duration} {unit}{duration > 1 && !unit.endsWith("s") ? "s" : ""}{isEvent ? ` · ${quantity} unité${quantity > 1 ? "s" : ""}` : ""}{driver ? ` · ${driver}` : ""}</>
              : <>Sélectionne une <strong>date de début</strong> dans le calendrier ci-dessus.</>}
          </div>
          <button className="confirm" data-testid="bd-booking-submit" disabled={sending || !!confirmed} onClick={submit}>
            {sending ? "Réservation…" : confirmed ? "Réservation confirmée ✅" : `Confirmer · ${fmt(total)}`}
          </button>
          {confirmed && (
            <div className="pp-confirm" data-testid="bd-booking-confirmed">
              <CheckCircle2 size={20} color="#2EE27B" />
              <div>
                <h3>C'est réservé ✅</h3>
                <p>{bien.name} · du {confirmed.start_date} au {confirmed.end_date}<br />Réf : <strong>{confirmed.booking_id}</strong> — le loueur te contactera pour finaliser.</p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
